import { AppConstant, KeyConstant, SystemConstant } from "const";
import { getInteractor } from "services/local.service";
import { toCamel, uuid, convertString2JSON, isE2E, isNewIvsE2EDevice, StorageUtil } from "utils";
import store, { SystemActions } from "redux-store";

const MAX_DIV_RESEND = 4;
/**
 *
 * @param {string} prefixKey
 * @param group
 * @param message
 * @param encryptTypeProtocol
 * @param encryptStatusType
 * @returns {Promise<null>}
 */
export const sendMessageEncryptStatus = async (prefixKey, group, message, encryptTypeProtocol, encryptStatusInfo) => {
  const { senderId, senderDeviceId, sourceId } = message || {};
  const groupId = group?.id || uuid();
  let updateValues = {};
  const messageOptions = message.options ? convertString2JSON(message.options, {}) : {};
  const device = await getInteractor(prefixKey).LocalDeviceService.get(senderDeviceId);
  if (device == null) return null;
  if (!isNewIvsE2EDevice(device)) {
    return null;
  }

  let encryptStatusType = encryptStatusInfo.type;

  let waitingIndex = 0;
  let lastIndex = 0;
  if (!messageOptions?.index_send_number) return null;
  const isResetSession = StorageUtil.getCommonKey(KeyConstant.KEY_RESET_SESSION);
  let apiIsResetSession = 0;
  if (encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.errorDistribute) {
    apiIsResetSession = 1;
    encryptStatusType = SystemConstant.ENCRYPT_STATUS_TYPE.errorDistribute;
  }
  if (
    encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.error ||
    encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.lostMessage ||
    isResetSession
  ) {
    if (isE2E(message.sendType, group.groupType)) {
      let sessionEntity = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
        senderId,
        senderDeviceId,
        groupId,
        encryptTypeProtocol,
      );
      let session = sessionEntity ? toCamel(sessionEntity) : null;
      if (session == null) {
        waitingIndex = messageOptions?.index_send_number || -1;
        lastIndex = messageOptions?.index_send_number + 1 || -1;
        encryptStatusType = SystemConstant.ENCRYPT_STATUS_TYPE.resetSession;
      } else {
        updateValues.retry_error = session?.retryError + 1;
        updateValues.state = 5;
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          senderId,
          senderDeviceId,
          message.groupId,
          encryptTypeProtocol,
        );
        waitingIndex = session?.waitingIndex;
        lastIndex = session?.lastIndex;
        let isSender = await getInteractor(prefixKey).LocalCryptoService.getSessionIsSender(
          senderId,
          senderDeviceId,
          message.groupId,
          encryptTypeProtocol,
        );
        if (
          isResetSession ||
          (encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.error &&
            sessionEntity?.retryError > MAX_DIV_RESEND &&
            isSender)
        ) {
          StorageUtil.setCommonKey(KeyConstant.KEY_RESET_SESSION, false);
          encryptStatusType = SystemConstant.ENCRYPT_STATUS_TYPE.resetSession;
          updateValues.retry_error = 1;
          updateValues.state = 0;
          if (waitingIndex > session.error_index) updateValues.error_index = waitingIndex;
          await getInteractor(prefixKey).LocalCryptoService.updateSession(
            updateValues,
            senderId,
            senderDeviceId,
            message.groupId,
            encryptTypeProtocol,
          );
        }
      }
    } else {
      const senderKeyEntity = await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        senderId,
        senderDeviceId,
        groupId,
        encryptTypeProtocol,
      );
      let senderKey = senderKeyEntity ? toCamel(senderKeyEntity) : null; // Check for null before toCamel
      if (!senderKey) {
        waitingIndex = messageOptions?.index_send_number || -1;
        lastIndex = messageOptions?.index_send_number + 1 || -1;
        apiIsResetSession = 1;
      } else {
        updateValues.retry_error = senderKey.retryError + 1;
        updateValues.state = 5;
        lastIndex = senderKey?.lastIndex;
        waitingIndex = senderKey?.waitingIndex;
        await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
          updateValues,
          senderId,
          senderDeviceId,
          groupId,
          encryptTypeProtocol,
        );
      }
    }
  } else {
    waitingIndex = messageOptions?.index_send_number || 0;
    if (isE2E(message.sendType, group.groupType)) {
      let sessionEntity = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
        senderId,
        senderDeviceId,
        groupId,
        encryptTypeProtocol,
      );
      let session = sessionEntity ? toCamel(sessionEntity) : null;
      lastIndex = session?.lastIndex;
    } else {
      const senderKeyEntity = await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        senderId,
        senderDeviceId,
        groupId,
        encryptTypeProtocol,
      );
      let senderKey = senderKeyEntity ? toCamel(senderKeyEntity) : null; // Check for null before toCamel
      lastIndex = senderKey?.lastIndex;
    }
  }

  let contentEncryptStatus = {
    encrypt_version: encryptTypeProtocol,
    source_id: sourceId,
    message_id: message.id,
    waiting_index: waitingIndex,
    encrypt_status_type: encryptStatusType,
    encrypt_status_code: encryptStatusInfo.code,
    encrypt_status_message: encryptStatusInfo.message,
    decrypt_send_type: message.sendType,
    last_index: lastIndex,
    is_reset_session: apiIsResetSession,
  };

  const options = { encryption_f: SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION };
  const encryptStatusMessage = {
    messageId: uuid(),
    sendToDeviceId: senderDeviceId,
    sendToAccountId: senderId,
    content: JSON.stringify(contentEncryptStatus),
    options: JSON.stringify(options),
    status: SystemConstant.MESSAGE_STATUS.send,
    sendType: SystemConstant.SEND_TYPE.encryptStatus,
    mentions: [],
    encryptType: SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION,
  };

  const newMessage = {
    isSendingKey: false,
    groupId: groupId,
    sourceId: uuid(),
    groupType: group.groupType,
    sendType: SystemConstant.SEND_TYPE.encryptStatus,
    created: Date.now(),
    messages: [encryptStatusMessage],
    branchId: message.branchId,
    parentId: null,
    threadId: null,
    messageId: uuid(),
    deviceList: [senderDeviceId],
  };

  await getInteractor(prefixKey).LocalApiCallService.save([
    {
      id: uuid(),
      task: `${AppConstant.API_CALL_TASK.message}`,
      original_uid: newMessage.sourceId,
      query: "",
      content: JSON.stringify({ data: newMessage }),
      original_content: JSON.stringify({ data: newMessage }),
      created: new Date().getTime(),
      retry: 0,
      branch_id: newMessage.branchId,
      group_id: newMessage.groupId,
    },
  ]);
  store.dispatch(SystemActions.executeQueue());
  return null;
};
